import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, Loader } from 'lucide-react';
import { checkInvitation, acceptInvitation } from '../services/invitationService';
import Navigation from '../components/Navigation';
import { Button, Card, Input } from '../components/ui';

const JoinViaInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });

  const [validationErrors, setValidationErrors] = useState({});

  const verifyInvitation = useCallback(async () => {
    try {
      const data = await checkInvitation(token);
      if (!data.isValid) {
        setError(`This invitation has already been ${data.status}. Please contact support for a new invitation.`);
      } else {
        setInvitation(data);
      }
    } catch (err) {
      setError(err.message || 'Invitation not found or has expired');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    verifyInvitation();
  }, [verifyInvitation]);

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await acceptInvitation(token, {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        password: formData.password
      });

      // Success - redirect to login with message
      setTimeout(() => {
        navigate('/login', {
          state: {
            message: `Welcome! Your account has been created. Please log in with your credentials.`,
            successEmail: invitation.email
          }
        });
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader className="w-12 h-12 animate-spin mx-auto mb-4 text-indigo-600" />
            <p className="text-gray-600">Verifying your invitation...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navigation />
        <div className="max-w-md mx-auto px-6 py-20">
          <Card className="border-red-200">
            <div className="flex gap-4">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-lg font-semibold text-red-900 mb-2">Invitation Issue</h2>
                <p className="text-sm text-red-700 mb-4">{error}</p>
                <Button
                  onClick={() => navigate('/')}
                  variant="primary"
                  className="w-full"
                >
                  Go Home
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </>
    );
  }

  if (!invitation) {
    return null;
  }

  return (
    <>
      <Navigation />
      <main className="max-w-md mx-auto px-6 py-10">
        <Card>
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-neutral-900 mb-2">
              Welcome to MHDF Momentum
            </h1>
            <p className="text-neutral-600">Create your account to get started</p>
          </div>

          {/* Invitation Info */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
            <p className="text-sm text-blue-900">
              <strong>Email:</strong> {invitation.email}
            </p>
            <p className="text-sm text-blue-900 mt-1">
              <strong>Account Type:</strong> {invitation.type === 'clinician' ? 'Clinician' : 'Client'}
            </p>
            <p className="text-sm text-blue-900 mt-1">
              <strong className="text-green-700">Free access while the platform is available</strong>
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                First Name *
              </label>
              <Input
                type="text"
                value={formData.firstName}
                onChange={(e) => {
                  setFormData({ ...formData, firstName: e.target.value });
                  if (validationErrors.firstName) {
                    setValidationErrors({ ...validationErrors, firstName: '' });
                  }
                }}
                placeholder="John"
                className={validationErrors.firstName ? 'border-red-500' : ''}
              />
              {validationErrors.firstName && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Last Name *
              </label>
              <Input
                type="text"
                value={formData.lastName}
                onChange={(e) => {
                  setFormData({ ...formData, lastName: e.target.value });
                  if (validationErrors.lastName) {
                    setValidationErrors({ ...validationErrors, lastName: '' });
                  }
                }}
                placeholder="Doe"
                className={validationErrors.lastName ? 'border-red-500' : ''}
              />
              {validationErrors.lastName && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.lastName}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Password *
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => {
                    setFormData({ ...formData, password: e.target.value });
                    if (validationErrors.password) {
                      setValidationErrors({ ...validationErrors, password: '' });
                    }
                  }}
                  placeholder="At least 8 characters"
                  className={validationErrors.password ? 'border-red-500' : ''}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {validationErrors.password && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.password}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Confirm Password *
              </label>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => {
                  setFormData({ ...formData, confirmPassword: e.target.value });
                  if (validationErrors.confirmPassword) {
                    setValidationErrors({ ...validationErrors, confirmPassword: '' });
                  }
                }}
                placeholder="Re-enter your password"
                className={validationErrors.confirmPassword ? 'border-red-500' : ''}
              />
              {validationErrors.confirmPassword && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.confirmPassword}</p>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              disabled={submitting}
              className="mt-6"
            >
              {submitting ? (
                <>
                  <Loader className="w-4 h-4 animate-spin mr-2" />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          {/* Info */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600">
              By creating an account, you agree to our Terms of Service and acknowledge our Privacy Policy.
              Your invitation remains valid until <strong>{new Date(invitation.expiresAt).toLocaleDateString()}</strong>.
            </p>
          </div>

          {/* Login Link */}
          <p className="text-center text-sm text-gray-600 mt-4">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-indigo-600 hover:underline font-medium"
            >
              Sign in
            </button>
          </p>
        </Card>
      </main>
    </>
  );
};

export default JoinViaInvitation;
